import React, { useState, useEffect } from 'react'
import { messagingService, MessageThread, UnreadStatus } from '../lib/messagingService'
import { supabase } from '../lib/supabase'
import { PressGaggleChat } from './PressGaggleChat'
import { GroupThreadChat } from './GroupThreadChat'
import { CreateGroupThread } from './CreateGroupThread'
import { useEventYear } from './EventYearContext'
import { User } from './AuthContext'

interface MessagingDashboardProps {
  currentUser: User
  onUnreadChange?: (hasUnread: boolean) => void
}

export const MessagingDashboard: React.FC<MessagingDashboardProps> = ({ currentUser, onUnreadChange }) => {
  const { currentYear } = useEventYear()
  const [activeThread, setActiveThread] = useState<string | null>(null)
  const [activeThreadType, setActiveThreadType] = useState<'press_gaggle' | 'group_thread' | null>(null)
  const [groupThreads, setGroupThreads] = useState<MessageThread[]>([])
  const [showCreateThread, setShowCreateThread] = useState(false)
  const [isLoadingThreads, setIsLoadingThreads] = useState(true)
  const [showSidebar, setShowSidebar] = useState(true)
  const [unreadStatus, setUnreadStatus] = useState<UnreadStatus>({})
  const [pressGaggleThreadId, setPressGaggleThreadId] = useState<string | null>(null)
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false)
  const [selectedThreads, setSelectedThreads] = useState<Set<string>>(new Set())
  
  const isPRTeam = currentUser.role === 'pr' || currentUser.role === 'admin'

  // Red dot component for unread indicators
  const RedDot: React.FC<{ hasUnread: boolean }> = ({ hasUnread }) => {
    if (!hasUnread) return null
    
    return (
      <span className="inline-flex h-2 w-2 bg-red-500 rounded-full"></span>
    )
  }

  // Load unread status
  const loadUnreadStatus = async () => {
    const status = await messagingService.getUnreadStatus(currentUser.id, currentYear)
    setUnreadStatus(status)
  }

  // Load group threads for user
  useEffect(() => {
    const loadGroupThreads = async () => {
      setIsLoadingThreads(true)
      const threads = await messagingService.getGroupThreadsForUser(currentUser.id)
      console.log('Loaded group threads:', threads) // Debug log
      setGroupThreads(threads)
      setIsLoadingThreads(false)
    }

    const loadPressGaggleId = async () => {
      const id = await messagingService.getPressGaggleThreadId()
      setPressGaggleThreadId(id)
    }

    loadGroupThreads()
    loadPressGaggleId()
    loadUnreadStatus()

    // Subscribe to real-time thread creation/updates
    const threadSubscription = messagingService.subscribeToThreadUpdates(currentUser.id, (updatedThread) => {
      setGroupThreads(prev => {
        const exists = prev.find(t => t.id === updatedThread.id)
        
        if (!updatedThread.is_active) {
          // Thread was deleted, remove from list
          const filtered = prev.filter(t => t.id !== updatedThread.id)
          // If deleted thread was active, switch to Press Gaggle
          if (activeThread === updatedThread.id) {
            setActiveThread('press_gaggle')
            setActiveThreadType('press_gaggle')
          }
          return filtered
        } else if (!exists && updatedThread.type === 'group_thread') {
          // New thread created, add to list
          return [...prev, updatedThread].sort((a, b) => 
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          )
        }
        
        return prev
      })
    })

    // Subscribe to new messages for unread count updates
    const messageSubscription = supabase
      .channel('message-counts')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        (payload) => {
          // If this is not the user's own message and not in the active thread, increment unread count
          if (payload.new.sender_id !== currentUser.id) {
            const messageThreadId = payload.new.thread_id
            const isActiveThread = (
              (activeThreadType === 'press_gaggle' && messageThreadId === pressGaggleThreadId) ||
              (activeThreadType === 'group_thread' && messageThreadId === activeThread)
            )
            
            if (!isActiveThread) {
              setUnreadStatus(prev => ({
                ...prev,
                [messageThreadId]: true
              }))
            }
          }
        }
      )
      .subscribe()

    return () => {
      if (threadSubscription) {
        messagingService.unsubscribe(threadSubscription)
      }
      if (messageSubscription) {
        supabase.removeChannel(messageSubscription)
      }
    }
  }, [currentUser.id])

  // Calculate and report total unread status
  useEffect(() => {
    const hasAnyUnread = Object.values(unreadStatus).some(status => status)
    onUnreadChange?.(hasAnyUnread)
  }, [unreadStatus, onUnreadChange])

  // Auto-select Press Gaggle if no thread is selected
  useEffect(() => {
    if (!activeThread) {
      setActiveThread('press_gaggle')
      setActiveThreadType('press_gaggle')
    }
  }, [activeThread])

  const handleThreadCreated = (threadId: string) => {
    setShowCreateThread(false)
    // Reload group threads
    messagingService.getGroupThreadsForUser(currentUser.id).then(setGroupThreads)
    // Switch to the new thread
    setActiveThread(threadId)
    setActiveThreadType('group_thread')
  }

  const handleDeleteThread = async (threadId: string, threadTitle: string) => {
    if (!isPRTeam) return
    
    const confirmed = window.confirm(`Are you sure you want to delete the "${threadTitle}" group thread? This action cannot be undone.`)
    
    if (confirmed) {
      const success = await messagingService.deleteGroupThread(threadId, currentUser.id)
      
      if (success) {
        // Remove from local state
        setGroupThreads(prev => prev.filter(t => t.id !== threadId))
        
        // If this was the active thread, switch to Press Gaggle
        if (activeThread === threadId) {
          setActiveThread('press_gaggle')
          setActiveThreadType('press_gaggle')
        }
      } else {
        alert('Failed to delete group thread. Please try again.')
      }
    }
  }

  const handleBulkDelete = async () => {
    if (!isPRTeam || selectedThreads.size === 0) return
    
    const threadsToDelete = groupThreads.filter(t => selectedThreads.has(t.id))
    const threadNames = threadsToDelete.map(t => t.title).join(', ')
    
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedThreads.size} group thread(s)?\n\n${threadNames}\n\nThis action cannot be undone.`
    )
    
    if (confirmed) {
      let deletedCount = 0
      
      for (const threadId of selectedThreads) {
        const success = await messagingService.deleteGroupThread(threadId, currentUser.id)
        if (success) {
          deletedCount++
        }
      }
      
      if (deletedCount > 0) {
        // Remove deleted threads from local state
        setGroupThreads(prev => prev.filter(t => !selectedThreads.has(t.id)))
        
        // If active thread was deleted, switch to Press Gaggle
        if (activeThread && selectedThreads.has(activeThread)) {
          setActiveThread('press_gaggle')
          setActiveThreadType('press_gaggle')
        }
        
        // Clear selection and exit bulk delete mode
        setSelectedThreads(new Set())
        setBulkDeleteMode(false)
        
        if (deletedCount === selectedThreads.size) {
          // All deletions successful
        } else {
          alert(`${deletedCount} of ${selectedThreads.size} threads were deleted successfully.`)
        }
      } else {
        alert('Failed to delete any threads. Please try again.')
      }
    }
  }

  const toggleThreadSelection = (threadId: string) => {
    setSelectedThreads(prev => {
      const newSet = new Set(prev)
      if (newSet.has(threadId)) {
        newSet.delete(threadId)
      } else {
        newSet.add(threadId)
      }
      return newSet
    })
  }

  const selectAllThreads = () => {
    setSelectedThreads(new Set(groupThreads.map(t => t.id)))
  }

  const clearSelection = () => {
    setSelectedThreads(new Set())
  }

  const handleSelectThread = async (threadId: string, type: 'press_gaggle' | 'group_thread') => {
    setActiveThread(threadId)
    setActiveThreadType(type)
    // Hide sidebar on mobile when thread is selected
    setShowSidebar(false)
    
    // Mark thread as read
    const actualThreadId = type === 'press_gaggle' ? pressGaggleThreadId : threadId
    if (actualThreadId) {
      await messagingService.markThreadAsRead(actualThreadId, currentUser.id)
      // Update unread status
      setUnreadStatus(prev => ({
        ...prev,
        [actualThreadId]: false
      }))
    }
  }

  return (
    <div className="flex bg-white" style={{ height: '100vh' }}>
      {/* Thread List Sidebar */}
      <div className={`${showSidebar ? 'w-full md:w-80' : 'hidden md:w-80 md:flex'} border-r border-gray-200 flex flex-col`} style={{ height: '100vh' }}>
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
            <div className="flex items-center space-x-4">
              {isPRTeam && !bulkDeleteMode && (
                <>
                  <button
                    onClick={() => setShowCreateThread(true)}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation"
                    title="Create Group Thread"
                  >
                    + New
                  </button>
                  {groupThreads.length > 0 && (
                    <button
                      onClick={() => setBulkDeleteMode(true)}
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 border border-red-700 touch-manipulation"
                      title="Delete Group Threads"
                    >
                      🗑️ Delete
                    </button>
                  )}
                </>
              )}
              {isPRTeam && bulkDeleteMode && (
                <>
                  <button
                    onClick={selectAllThreads}
                    className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                    title="Select All"
                  >
                    All
                  </button>
                  <button
                    onClick={clearSelection}
                    className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                    title="Clear Selection"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={selectedThreads.size === 0}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={`Delete ${selectedThreads.size} selected thread(s)`}
                  >
                    Delete ({selectedThreads.size})
                  </button>
                  <button
                    onClick={() => {
                      setBulkDeleteMode(false)
                      setSelectedThreads(new Set())
                    }}
                    className="px-3 py-1 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    title="Cancel"
                  >
                    Cancel
                  </button>
                </>
              )}
              {/* Back button for mobile */}
              {!showSidebar && (
                <button
                  onClick={() => setShowSidebar(true)}
                  className="md:hidden p-2 text-gray-500 hover:text-gray-700"
                  title="Show conversations"
                >
                  ←
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Thread List */}
        <div className="flex-1 overflow-y-scroll" style={{ maxHeight: '500px', overflowY: 'scroll' }}>
          {/* Press Gaggle - Always First */}
          <button
            onClick={() => handleSelectThread('press_gaggle', 'press_gaggle')}
            className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
              activeThread === 'press_gaggle' ? 'bg-pink-50 border-l-4 border-l-pink-500' : ''
            }`}
          >
            <div className="flex items-center">
              <div className="text-2xl mr-3">📢</div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">Press Gaggle</div>
                <div className="text-sm text-gray-600">Public discussion for all members</div>
              </div>
              <RedDot hasUnread={pressGaggleThreadId ? (unreadStatus[pressGaggleThreadId] || false) : false} />
            </div>
          </button>

          {/* Group Threads */}
          {isLoadingThreads ? (
            <div className="p-4 text-center text-gray-500">Loading threads...</div>
          ) : (
            <>
              {groupThreads.length > 0 && (
                <div className="p-3 bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-100">
                  Group Conversations
                </div>
              )}
              {groupThreads.map((thread) => (
                <div 
                  key={thread.id}
                  className={`relative border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    activeThread === thread.id ? 'bg-pink-50 border-l-4 border-l-pink-500' : ''
                  }`}
                >
                  <button
                    onClick={() => {
                      if (bulkDeleteMode) {
                        toggleThreadSelection(thread.id)
                      } else {
                        handleSelectThread(thread.id, 'group_thread')
                      }
                    }}
                    className="w-full p-4 text-left"
                  >
                    <div className="flex items-center">
                      {bulkDeleteMode && (
                        <div className="mr-3">
                          <input
                            type="checkbox"
                            checked={selectedThreads.has(thread.id)}
                            onChange={() => toggleThreadSelection(thread.id)}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                      <div className="text-2xl mr-3">👥</div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{thread.title}</div>
                        {thread.description && (
                          <div className="text-sm text-gray-600 truncate">{thread.description}</div>
                        )}
                      </div>
                      {!bulkDeleteMode && <RedDot hasUnread={unreadStatus[thread.id] || false} />}
                    </div>
                  </button>
                </div>
              ))}
            </>
          )}

          {/* Empty State for Group Threads */}
          {!isLoadingThreads && groupThreads.length === 0 && isPRTeam && (
            <div className="p-4 text-center text-gray-500">
              <div className="text-4xl mb-2">💬</div>
              <div className="text-sm">No group threads yet</div>
              <button
                onClick={() => setShowCreateThread(true)}
                className="text-pink-600 hover:text-pink-700 text-sm mt-1"
              >
                Create your first group thread
              </button>
            </div>
          )}
          
          {!isLoadingThreads && groupThreads.length === 0 && !isPRTeam && (
            <div className="p-4 text-center text-gray-500">
              <div className="text-4xl mb-2">💬</div>
              <div className="text-sm">No group conversations yet</div>
              <div className="text-xs text-gray-400 mt-1">
                PR team members will add you to relevant discussions
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${showSidebar ? 'hidden md:flex' : 'flex'} flex-1 flex-col max-h-screen min-h-0 relative`}>
        {/* Mobile back button */}
        {activeThread && (
          <button
            onClick={() => setShowSidebar(true)}
            className="md:hidden absolute top-4 left-4 z-10 p-3 bg-white rounded-full shadow-md text-gray-600 hover:text-gray-800 touch-manipulation"
            title="Back to conversations"
          >
            ←
          </button>
        )}
        
        {activeThreadType === 'press_gaggle' && (
          <PressGaggleChat currentUser={currentUser} />
        )}
        
        {activeThreadType === 'group_thread' && activeThread && (
          <GroupThreadChat 
            threadId={activeThread} 
            currentUser={currentUser} 
          />
        )}
        
        {!activeThread && (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <div className="text-6xl mb-4">💬</div>
              <div className="text-xl">Select a conversation to start messaging</div>
            </div>
          </div>
        )}
      </div>

      {/* Create Group Thread Modal */}
      {showCreateThread && (
        <CreateGroupThread
          currentUser={currentUser}
          onThreadCreated={handleThreadCreated}
          onCancel={() => setShowCreateThread(false)}
        />
      )}
    </div>
  )
}